import { toSvg } from "html-to-image";

import { MAX_ATTACHMENT_SIZE_MB } from "../hooks/use-image-attachments";

/** Bu niteliği taşıyan düğümler yakalanan görüntüye dahil edilmez */
export const SCREENSHOT_EXCLUDE_ATTR = "data-screenshot-exclude";

/**
 * Gerçek pikselleri veren `getDisplayMedia` hiçbir mobil tarayıcıda
 * (iOS/Android fark etmeksizin) desteklenmiyor — bu bir WebKit/Chromium
 * kısıtı değil, platform sağlayıcılarının (Apple/Google) mobilde hiç
 * uygulamadığı bir API. Bu yüzden mobilde DOM tabanlı yakalamaya
 * (`captureViaDom`) düşülür; bu yol CORS'lu görselleri ve iç içe scroll
 * konumlarını yaklaşık olarak taklit ettiğinden bire bir doğru olmayabilir.
 * Mobilde "ekranı yakala" seçeneğini göstermek yerine, kullanıcının
 * cihazının kendi native ekran görüntüsünü alıp galeriden eklemesi
 * (mevcut "Görsel yükle" akışı) çok daha güvenilir olduğundan arayüz bu
 * bilgiye göre seçeneği gizler.
 */
export const isScreenCaptureSupported = (): boolean =>
  typeof navigator !== "undefined" && !!navigator.mediaDevices?.getDisplayMedia;

/** Uzun kenar üst sınırı — retina ekranlarda dosya boyutunu makul tutar */
const MAX_DIMENSION = 1920;
const MAX_FILE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;
const QUALITY_STEPS = [0.9, 0.75, 0.6, 0.45];
/** Yakalama takılırsa arayüz süresiz beklemesin */
const CAPTURE_TIMEOUT_MS = 20_000;

/**
 * CORS başlığı göndermeyen sunuculardaki görseller (örn. bazı GCS bucket'ları)
 * fetch edilemediği için gömülemez; şeffaf piksel altındaki UI rengini
 * gösterip kafa karıştırdığından nötr gri bir dolgu kullanılır.
 */
const IMAGE_PLACEHOLDER_PIXEL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADklEQVR4AWJ69OTlfwAAAAD//0In6usAAAAGSURBVAMACRQDsUcO/5EAAAAASUVORK5CYII=";

/**
 * WebKit (Safari/iOS), SVG'yi canvas'a çizerken vektörel olarak hedef
 * boyutta rasterize eder — ölçek hilesine gerek yoktur, üstelik hile
 * orada kenar artefaktları üretir. Chrome ise SVG'yi intrinsic boyutta
 * bitmap'leyip büyüttüğünden bulanıklık ve kenar şeridi oluşturur; orada
 * içerik SVG içinde transform'la büyütülmelidir.
 */
const IS_WEBKIT =
  typeof navigator !== "undefined" &&
  /AppleWebKit/i.test(navigator.userAgent) &&
  !/Chrome|Chromium|Edg\//i.test(navigator.userAgent);

/** Serileştirmeyi bozabilen, görsel katkısı olmayan düğümler */
const ALWAYS_SKIPPED_TAGS = new Set(["SCRIPT", "NOSCRIPT", "LINK"]);
/**
 * Kirlenmiş (tainted) canvas'ta html-to-image `toDataURL` çağrısını
 * korumasız yaptığı için bu etiketler tüm yakalamayı düşürebiliyor;
 * yalnızca son çare denemesinde dışarıda bırakılırlar.
 */
const MEDIA_TAGS = new Set(["CANVAS", "VIDEO", "IFRAME"]);

const isExcluded = (node: HTMLElement): boolean =>
  typeof node.getAttribute === "function" &&
  node.getAttribute(SCREENSHOT_EXCLUDE_ATTR) !== null;

interface FilterLevel {
  skipMedia: boolean;
  skipImages: boolean;
}

const buildFilter = (level: FilterLevel) => (node: HTMLElement) => {
  // SVG ad alanındaki düğümler tagName'i küçük harfle bildirir
  const tagName = node.tagName?.toUpperCase();
  if (tagName) {
    if (ALWAYS_SKIPPED_TAGS.has(tagName)) return false;
    if (level.skipMedia && MEDIA_TAGS.has(tagName)) return false;
    // Satır içi ikonlar boyuta katkı vermediği için yalnızca <img> atılır
    if (level.skipImages && tagName === "IMG") return false;
  }
  return !isExcluded(node);
};

/**
 * Yakalamaya girmemesi gereken düğümleri (mod çubuğu, toast'lar) geçici
 * olarak gizler. DOM yakalamada filtre bunu zaten yapıyor; ekran yakalama
 * API'si gerçek pikselleri okuduğu için gizleme fiziksel olmalı.
 */
const hideExcludedNodes = (): (() => void) => {
  const nodes = Array.from(
    document.querySelectorAll<HTMLElement>(`[${SCREENSHOT_EXCLUDE_ATTR}]`),
  );
  const previousVisibility = nodes.map((node) => node.style.visibility);
  nodes.forEach((node) => {
    node.style.visibility = "hidden";
  });
  return () => {
    nodes.forEach((node, index) => {
      node.style.visibility = previousVisibility[index];
    });
  };
};

/**
 * html-to-image, DOM'u klonlarken elementlerin scrollTop/scrollLeft
 * değerlerini taşımaz; kaydırılabilir tüm alanlar görüntüde en başa döner
 * (özellikle mobilde sayfa hep "hiç kaydırılmamış" haliyle çıkar).
 *
 * Çözüm: kaydırılmış konteynerlerin çocuklarını geçici bir translate ile
 * kaydırılmış konumuna taşımak. Transform layout'u etkilemediği ve klon
 * konteynerin overflow kırpması korunduğu için sonuç, kullanıcının o an
 * gördüğü görünümle eşleşir. Dönen fonksiyon değişiklikleri geri alır.
 *
 * Not: documentElement/body bilinçli olarak atlanır — transform'lu ata,
 * `position: fixed` elemanların referansını bozar; pencere kaydırması
 * durumunda zaten tüm sayfa yüksekliği yakalandığı için içerik kaybolmaz.
 */
const emulateScrollPositions = (): (() => void) => {
  const restores: Array<() => void> = [];

  document.body.querySelectorAll<HTMLElement>("*").forEach((element) => {
    const { scrollTop, scrollLeft } = element;
    if (!scrollTop && !scrollLeft) return;

    Array.from(element.children).forEach((child) => {
      if (!(child instanceof HTMLElement)) return;
      // Fixed elemanlar kaydırmadan etkilenmez; taşınırlarsa yanlış yere gider
      if (getComputedStyle(child).position === "fixed") return;

      const previousTransform = child.style.transform;
      child.style.transform = `${
        previousTransform ? `${previousTransform} ` : ""
      }translate(${-scrollLeft}px, ${-scrollTop}px)`;
      restores.push(() => {
        child.style.transform = previousTransform;
      });
    });
  });

  return () => restores.forEach((restore) => restore());
};

/** Tek bir görsel fetch'i takılırsa tüm yakalamayı bekletmesin */
const IMAGE_INLINE_TIMEOUT_MS = 8_000;

/**
 * Cross-origin görselleri yakalamadan önce geçici blob URL'lerine çevirir.
 *
 * html-to-image her görseli CORS'lu fetch ile indirip gömmek zorunda;
 * Safari/iOS ise `<img>` üzerinden CORS'suz indirilip önbelleğe girmiş
 * yanıtı bu fetch'e başlıksız verip isteği düşürebiliyor (`cache:
 * "no-cache"` bile bellek önbelleğine her zaman işlemiyor). Önbelleği
 * atlatan bir parametreyle kendimiz indirip src'yi blob URL ile
 * değiştirince görsel aynı origin'e taşınmış olur ve tartışma kalmaz.
 *
 * CORS'a hiç izin vermeyen sunuculardaki görseller (fetch reddedilir)
 * olduğu gibi bırakılır ve gri dolguya düşer. Dönen fonksiyon tüm src
 * değişikliklerini geri alır.
 */
const inlineCrossOriginImages = async (): Promise<() => void> => {
  const restores: Array<() => void> = [];
  const blobUrls: string[] = [];

  const crossOriginImages = Array.from(
    document.body.querySelectorAll("img"),
  ).filter((img) => {
    const src = img.currentSrc || img.src;
    if (!src || src.startsWith("data:") || src.startsWith("blob:")) {
      return false;
    }
    try {
      return new URL(src, window.location.href).origin !== window.location.origin;
    } catch {
      return false;
    }
  });

  await Promise.all(
    crossOriginImages.map(async (img) => {
      const src = img.currentSrc || img.src;
      try {
        const bustUrl = new URL(src, window.location.href);
        bustUrl.searchParams.set("hti-cache-bust", Date.now().toString(36));

        const response = await fetch(bustUrl.href, {
          mode: "cors",
          cache: "no-store",
          signal: AbortSignal.timeout(IMAGE_INLINE_TIMEOUT_MS),
        });
        if (!response.ok) return;

        const blobUrl = URL.createObjectURL(await response.blob());
        blobUrls.push(blobUrl);

        // srcset kaldırılmazsa tarayıcı src yerine onu kullanmaya devam eder
        const previousSrc = img.getAttribute("src");
        const previousSrcset = img.getAttribute("srcset");
        img.removeAttribute("srcset");
        img.src = blobUrl;
        restores.push(() => {
          if (previousSrcset !== null) img.setAttribute("srcset", previousSrcset);
          if (previousSrc !== null) img.setAttribute("src", previousSrc);
          else img.removeAttribute("src");
        });

        await img.decode().catch(() => undefined);
      } catch {
        // CORS reddi ya da ağ hatası — görsel gri dolguya düşer
      }
    }),
  );

  return () => {
    restores.forEach((restore) => restore());
    blobUrls.forEach((url) => URL.revokeObjectURL(url));
  };
};

/**
 * Scroll emülasyonu canlı sayfada içeriği görünür şekilde oynattığı için
 * yakalama süresince ekran, görüntüye girmeyen opak bir örtüyle kapatılır.
 */
const showCaptureCover = (background: string): (() => void) => {
  const cover = document.createElement("div");
  cover.setAttribute(SCREENSHOT_EXCLUDE_ATTR, "");
  cover.style.cssText = `position: fixed; inset: 0; z-index: 2147483647; background: ${background};`;
  document.body.appendChild(cover);
  return () => cover.remove();
};

const withTimeout = <T,>(promise: Promise<T>, ms: number): Promise<T> => {
  let timer: ReturnType<typeof setTimeout>;
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => {
      timer = setTimeout(() => reject(new Error("capture-timeout")), ms);
    }),
  ]).finally(() => clearTimeout(timer)) as Promise<T>;
};

const readBackgroundColor = (): string => {
  const bodyBackground = getComputedStyle(document.body).backgroundColor;
  // Şeffaf gövdede JPEG siyah çıkar; kök elemana veya beyaza düşülür
  if (bodyBackground && !bodyBackground.startsWith("rgba(0, 0, 0, 0)")) {
    return bodyBackground;
  }
  const rootBackground = getComputedStyle(
    document.documentElement,
  ).backgroundColor;
  return rootBackground && !rootBackground.startsWith("rgba(0, 0, 0, 0)")
    ? rootBackground
    : "#ffffff";
};

const formatBytes = (length: number): string =>
  `${(length / (1024 * 1024)).toFixed(2)} MB`;

/**
 * SVG data URL'ini görsele çevirir.
 *
 * html-to-image bu adımı kendi yapıyor ama hatayı ham `Event` olarak
 * fırlattığı için sebebi anlaşılmıyor. Burada veri boyutunu da mesaja
 * koyarak teşhis edilebilir bir hata üretiyoruz — bu adımın en sık
 * sebebi data URL'in tarayıcı sınırını aşacak kadar büyümesi.
 */
const loadSvgImage = (dataUrl: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.decoding = "sync";
    image.onload = () => resolve(image);
    image.onerror = () =>
      reject(
        new Error(
          `SVG görüntüye çevrilemedi (data URL ${formatBytes(dataUrl.length)})`,
        ),
      );
    image.src = dataUrl;
  });

/** SVG görüntüyü hedef boyutta opak zemine çizer */
const drawToCanvas = (
  image: HTMLImageElement,
  targetWidth: number,
  targetHeight: number,
  background: string,
): HTMLCanvasElement => {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, targetWidth);
  canvas.height = Math.max(1, targetHeight);

  const context = canvas.getContext("2d");
  if (!context) throw new Error("canvas-context-unavailable");

  context.fillStyle = background;
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  return canvas;
};

const toBlob = (canvas: HTMLCanvasElement, quality: number): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("encode-failed"))),
      "image/jpeg",
      quality,
    );
  });

/** Ek boyutu sınırının altına inene kadar kaliteyi kademeli düşürür */
const encodeWithinLimit = async (canvas: HTMLCanvasElement): Promise<Blob> => {
  let lastBlob: Blob | null = null;

  for (const quality of QUALITY_STEPS) {
    lastBlob = await toBlob(canvas, quality);
    if (lastBlob.size <= MAX_FILE_BYTES) return lastBlob;
  }

  if (!lastBlob) throw new Error("encode-failed");
  return lastBlob;
};

/**
 * Sayfa yeniden çizildiği için font, görsel ve gömülü medyanın hepsinin
 * serileştirilmesi gerekir; her biri CORS ya da kirlenmiş canvas yüzünden
 * tüm yakalamayı düşürebilir. Bu yüzden giderek daha çok şeyden feragat
 * eden denemeler sırayla uygulanır.
 */
const CAPTURE_ATTEMPTS = [
  { label: "full", skipFonts: false, skipMedia: false, skipImages: false },
  { label: "no-fonts", skipFonts: true, skipMedia: false, skipImages: false },
  {
    label: "no-fonts-no-media",
    skipFonts: true,
    skipMedia: true,
    skipImages: false,
  },
  // Son çare: gömülü görseller data URL'i şişirdiği için hepsi atılır;
  // düzen ve metin korunur, görsel kutuları boş kalır
  {
    label: "layout-only",
    skipFonts: true,
    skipMedia: true,
    skipImages: true,
  },
];

const toScreenshotFile = (blob: Blob): File =>
  new File([blob], `screenshot-${Date.now()}.jpg`, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });

/**
 * Tarayıcının ekran yakalama API'siyle mevcut sekmenin GERÇEK piksellerini
 * alır. DOM yakalamanın aksine CORS kısıtı yoktur: cross-origin görseller,
 * video kareleri ve canvas içerikleri eksiksiz görünür.
 *
 * Chrome/Edge `preferCurrentTab` sayesinde seçiciyi "Bu sekme" öntanımlı
 * açar; kullanıcı tek tıkla onaylar. Desteklenmiyor ya da reddedildiyse
 * `null` döner ve DOM yakalamaya düşülür.
 */
const captureViaDisplayMedia = async (): Promise<File | null> => {
  if (!navigator.mediaDevices?.getDisplayMedia) return null;

  const restoreExcludedNodes = hideExcludedNodes();
  let stream: MediaStream | null = null;
  const video = document.createElement("video");

  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: "browser" },
      audio: false,
      // Chrome'a özgü ipuçları — diğer tarayıcılar bilinmeyen alanları yok sayar
      preferCurrentTab: true,
      selfBrowserSurface: "include",
      surfaceSwitching: "exclude",
      monitorTypeSurfaces: "exclude",
    } as DisplayMediaStreamOptions);

    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    await video.play();

    // Boş/siyah kare almamak için ilk gerçek karenin çizilmesini bekle
    await withTimeout(
      new Promise<void>((resolve) => {
        if ("requestVideoFrameCallback" in video) {
          (
            video as HTMLVideoElement & {
              requestVideoFrameCallback: (cb: () => void) => void;
            }
          ).requestVideoFrameCallback(() => resolve());
        } else {
          setTimeout(resolve, 300);
        }
      }),
      5_000,
    );

    const frameWidth = video.videoWidth;
    const frameHeight = video.videoHeight;
    if (!frameWidth || !frameHeight) return null;

    const scale = Math.min(1, MAX_DIMENSION / Math.max(frameWidth, frameHeight));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(frameWidth * scale));
    canvas.height = Math.max(1, Math.round(frameHeight * scale));

    const context = canvas.getContext("2d");
    if (!context) return null;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const blob = await encodeWithinLimit(canvas);
    return toScreenshotFile(blob);
  } catch (error) {
    // Kullanıcı seçiciyi kapattıysa ya da API desteklenmiyorsa sessizce
    // DOM yakalamaya düş; diğer hatalar da aynı yola çıkar
    console.warn("[screenshot] Ekran yakalama API'si kullanılamadı", error);
    return null;
  } finally {
    video.srcObject = null;
    stream?.getTracks().forEach((track) => track.stop());
    restoreExcludedNodes();
  }
};

/** DOM'u SVG üzerinden rasterize eden yedek yakalama yolu */
const captureViaDom = async (): Promise<File> => {
  const backgroundColor = readBackgroundColor();
  const width = document.body.offsetWidth || window.innerWidth;
  const height = document.body.offsetHeight || window.innerHeight;

  // Retina netliği için DPR'a kadar büyüt, dosya boyutu için uzun kenarı sınırla
  const scale = Math.min(
    Math.min(window.devicePixelRatio || 1, 2),
    MAX_DIMENSION / Math.max(width, height),
  );
  const targetWidth = Math.round(width * scale);
  const targetHeight = Math.round(height * scale);
  let lastError: unknown;

  // Klon scrollTop bilgisini taşımadığından kaydırılmış görünüm transform
  // ile taklit edilir; canlı sayfadaki oynama örtüyle gizlenir
  const restoreScrollPositions = emulateScrollPositions();
  const removeCover = showCaptureCover(backgroundColor);
  // Örtü açıkken görseller blob'a çevrilir; olası src flicker'ı görünmez
  const restoreImageSources = await inlineCrossOriginImages();

  try {
    for (const attempt of CAPTURE_ATTEMPTS) {
      try {
        const dataUrl = await withTimeout(
          toSvg(document.body, {
            backgroundColor,
            imagePlaceholder: IMAGE_PLACEHOLDER_PIXEL,
            skipFonts: attempt.skipFonts,
            filter: buildFilter(attempt),
            // Safari, <img> ile CORS'suz indirilip önbelleğe girmiş görseli
            // CORS'lu fetch'e başlıksız verip isteği düşürüyor; önbelleği
            // atlayarak taze (başlıklı) yanıt alınır
            fetchRequestInit: { cache: "no-cache" },
            ...(IS_WEBKIT
              ? // WebKit vektörel çizdiği için CSS boyutu yeterli
                { width, height }
              : {
                  // Chrome: SVG intrinsic boyutu hedef piksel boyutu olur;
                  // içerik transform ile büyütüldüğü için foreignObject tam
                  // çözünürlükte rasterize edilir
                  width: targetWidth,
                  height: targetHeight,
                  style: {
                    transform: `scale(${scale})`,
                    transformOrigin: "top left",
                    width: `${width}px`,
                    height: `${height}px`,
                  },
                }),
          }),
          CAPTURE_TIMEOUT_MS,
        );

        const image = await withTimeout(
          loadSvgImage(dataUrl),
          CAPTURE_TIMEOUT_MS,
        );
        const canvas = drawToCanvas(
          image,
          targetWidth,
          targetHeight,
          backgroundColor,
        );
        const blob = await encodeWithinLimit(canvas);
        return toScreenshotFile(blob);
      } catch (error) {
        lastError = error;
        console.warn(
          `[screenshot] "${attempt.label}" denemesi başarısız`,
          error,
        );
      }
    }
  } finally {
    restoreImageSources();
    removeCover();
    restoreScrollPositions();
  }

  throw lastError instanceof Error ? lastError : new Error("capture-failed");
};

/**
 * Görünen ekranı yakalar.
 *
 * Önce tarayıcının ekran yakalama API'si denenir: gerçek pikseller
 * alındığı için CORS engelli görseller de (örn. farklı origin'den gelen
 * model çıktıları) eksiksiz görünür. Kullanıcı reddederse ya da tarayıcı
 * desteklemiyorsa DOM tabanlı rasterizasyona düşülür.
 */
export const captureViewport = async (): Promise<File> => {
  const nativeCapture = await captureViaDisplayMedia();
  if (nativeCapture) return nativeCapture;
  return captureViaDom();
};
